import { Component } from 'react'
import PropTypes from 'prop-types'
import { nanoid } from '@reduxjs/toolkit'
import { noop } from 'lodash-es'
import TimetoastPropTypes from '@constants/timetoast_prop_types.js'
import Form from '@components/forms/form.jsx'
import FormGroup from '@components/forms/form_group.jsx'
import Label from '@components/forms/label.jsx'
import Input from '@components/forms/input.jsx'
import Select from '@components/forms/select.jsx'
import Link from '@components/Link.tsx'
import Hint from '@components/Hint.tsx'
import ColorPalette from '@components/ColorPalette.tsx'
import FloatingModal from '@components/modals/FloatingModal.tsx'
import { DEFAULT, TIMELINE, LIST } from '@constants/timeline_view_modes.js'
import { defaultColorScheme } from '@helpers/color_scheme_helpers.js'

import ColorSchemeCustomizer from './colorscheming/color_scheme_customizer.jsx'

export default class EmbedForm extends Component {
  constructor(props) {
    super(props)
    this.contextIdentifier = nanoid()
    this.state = {
      showColorCustomizer: false,
    }
  }

  handleFieldChange = event => {
    const { name, value } = event
    this.props.onFieldChange(name, value)
  }

  handleColorSchemeChange = colorScheme => {
    const { onColorSchemeChange } = this.props
    onColorSchemeChange(colorScheme)
  }

  handleColorPaletteClick = e => {
    e.preventDefault()
    this.showColorCustomizer()
  }

  showColorCustomizer = () => {
    this.setState({
      showColorCustomizer: true,
    })
  }

  hideColorCustomizer = () => {
    this.setState({
      showColorCustomizer: false,
    })
  }

  getSelectOptions() {
    // Note: If this is set as default no 'mode' query string will be added to the embed code
    return [
      { text: 'Default', value: DEFAULT },
      //{text: "Timeline", value: TIMELINE},
      { text: 'List', value: LIST },
    ]
  }

  contextualize(fieldName) {
    return `${fieldName}-${this.contextIdentifier}`
  }

  getColorCustomizerContent() {
    const { colorScheme, colorCustomization } = this.props
    const { showColorCustomizer } = this.state

    return (
      <FloatingModal
        title="Customize colors"
        show={showColorCustomizer}
        variant="whiteout"
        onClose={this.hideColorCustomizer}
        className="qa-color-scheme-customizer-modal"
      >
        <ColorSchemeCustomizer
          colorScheme={colorScheme}
          onChange={this.handleColorSchemeChange}
          colorCustomization={colorCustomization}
        />
      </FloatingModal>
    )
  }

  getColorPaletteContent() {
    const { colorScheme, colorCustomization } = this.props
    let content = null
    if (colorCustomization) {
      const colorCustomizer = this.getColorCustomizerContent()
      content = (
        <>
          <Link
            onClick={this.handleColorPaletteClick}
            className="embed-customizer__color-palette embed-customizer__color-palette--editable cursor--pointer"
          >
            <ColorPalette colorScheme={colorScheme} />
          </Link>
          {colorCustomizer}
        </>
      )
    } else {
      content = (
        <>
          <p className="tip">Upgrade plan to change color scheme.</p>
          <div className="embed-customizer__color-palette">
            <ColorPalette colorScheme={colorScheme} />
          </div>
        </>
      )
    }
    return (
      <FormGroup className="form-group">
        <Label className="label">Color scheme</Label>
        {content}
      </FormGroup>
    )
  }

  render() {
    const { width, height, mode, colorScheme } = this.props
    const selectOptions = this.getSelectOptions()
    const colorPalette = this.getColorPaletteContent()
    return (
      <Form
        className="form form--limited-inputs"
        onSubmit={e => {
          e.preventDefault()
        }}
      >
        <FormGroup className="form-group">
          <Label className="label" htmlFor={this.contextualize('mode')}>
            Timeline mode <Hint hint="List mode overrides the default horizontal timeline." />
          </Label>
          <Select
            id={this.contextualize('mode')}
            name="mode"
            options={selectOptions}
            value={mode}
            onChange={this.handleFieldChange}
            className="select select--medium"
          />
        </FormGroup>

        <FormGroup className="form-group">
          <Label className="label">Size</Label>
          <div className="embed-customizer-size-items">
            <Input
              id={this.contextualize('width')}
              name="width"
              value={width.toString()}
              onChange={this.handleFieldChange}
              className="input embed-customizer-size-items__item"
            />
            <span className="times embed-customizer-size-items__item">×</span>
            <Input
              id={this.contextualize('height')}
              name="height"
              value={height.toString()}
              onChange={this.handleFieldChange}
              className="input embed-customizer-size-items__item"
            />
          </div>
        </FormGroup>

        {colorPalette}
      </Form>
    )
  }
}
EmbedForm.propTypes = {
  width: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,
  mode: PropTypes.string.isRequired,
  colorScheme: TimetoastPropTypes.colorScheme.isRequired,
  colorCustomization: PropTypes.bool.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onColorSchemeChange: PropTypes.func.isRequired,
}
EmbedForm.defaultProps = {
  width: '500',
  height: '440',
  mode: DEFAULT,
  colorScheme: defaultColorScheme(),
  colorCustomization: false,
  onFieldChange: noop,
  onColorSchemeChange: noop,
}
